import React, { useEffect, useMemo, useState } from 'react'

import { Icon } from '@/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { faCartShopping } from '@fortawesome/pro-light-svg-icons/faCartShopping'
import { faCircleUser } from '@fortawesome/pro-light-svg-icons/faCircleUser'
import { Box, Fade, styled, Typography } from '@mui/material'

import type { LiveActivityData } from './types'

const DEFAULT_NAMES = [
  'Emma', 'Sarah', 'Mei', 'Lin', 'Aisha', 'Priya', 'Sofia', 'Hannah',
  'Olivia', 'Yuki', 'Zara', 'Maya', 'Chloe', 'Anya', 'Jasmine', 'Hina',
  'Grace', 'Naomi', 'Ava', 'Nora', 'Riya', 'Luna', 'Ivy', 'Mia',
  'Ling', 'Ella', 'Ruby', 'Isla', 'Amara', 'Thuy',
]

const DEFAULT_CITIES = [
  'Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide',
  'Auckland', 'Singapore', 'Kuala Lumpur', 'Hong Kong',
  'Tokyo', 'Seoul', 'San Francisco', 'Toronto', 'London', 'Vancouver',
]

const DEFAULT_TEMPLATES = [
  '{name} from {city} just personalised this',
  '{name} added this to their cart {time}',
  'Purchased {time} by {name}',
  '{name} in {city} got their copy {time}',
  '{name} personalised this {time}',
]

const TIME_PHRASES = [
  'just now',
  '2 mins ago',
  '4 mins ago',
  '7 mins ago',
  '11 mins ago',
  '18 mins ago',
  '26 mins ago',
  '38 mins ago',
  'about an hour ago',
]

const ROTATION_MS = 5500
const FADE_MS = 260
const VIEWER_DRIFT_MS = 35000
const VIEWER_DRIFT_CHANCE = 0.55

const DEFAULT_VIEWERS: [number, number] = [7, 12]
const DEFAULT_CART: [number, number] = [2, 4]

function hashCode(s: string): number {
  let h = 0

  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0
  }

  return Math.abs(h)
}

function pick<T>(arr: T[], i: number): T {
  return arr[((i % arr.length) + arr.length) % arr.length]
}

function randomInRange(min: number, max: number): number {
  if (max <= min) return min

  return min + Math.floor(Math.random() * (max - min + 1))
}

interface LiveActivityTickerProps {
  storySlug: string
  data?: LiveActivityData
}

export const LiveActivityTicker: React.FC<LiveActivityTickerProps> = ({ storySlug, data }) => {
  const names = data?.names?.length ? data.names : DEFAULT_NAMES
  const cities = data?.cities?.length ? data.cities : DEFAULT_CITIES
  const templates = data?.message_templates?.length ? data.message_templates : DEFAULT_TEMPLATES
  const [viewerMin, viewerMax] = data?.viewers_range ?? DEFAULT_VIEWERS
  const [cartMin, cartMax] = data?.cart_count_range ?? DEFAULT_CART

  const seed = useMemo(() => hashCode(storySlug), [storySlug])

  const [mounted, setMounted] = useState(false)
  const [tickIndex, setTickIndex] = useState(0)
  const [show, setShow] = useState(true)
  const [viewerCount, setViewerCount] = useState(viewerMin)
  const [cartCount, setCartCount] = useState(cartMin)

  useEffect(() => {
    setMounted(true)
    setViewerCount(randomInRange(viewerMin, viewerMax))
    setCartCount(randomInRange(cartMin, cartMax))
  }, [viewerMin, viewerMax, cartMin, cartMax])

  useEffect(() => {
    if (!mounted) return undefined

    const id = window.setInterval(() => {
      setShow(false)
      window.setTimeout(() => {
        setTickIndex((i) => i + 1)
        setShow(true)
      }, FADE_MS)
    }, ROTATION_MS)

    return () => window.clearInterval(id)
  }, [mounted])

  useEffect(() => {
    if (!mounted) return undefined

    const id = window.setInterval(() => {
      if (Math.random() > VIEWER_DRIFT_CHANCE) return

      setViewerCount((v) => {
        const delta = Math.random() < 0.5 ? -1 : 1
        const next = v + delta

        if (next < viewerMin) return viewerMin

        if (next > viewerMax) return viewerMax

        return next
      })
    }, VIEWER_DRIFT_MS)

    return () => window.clearInterval(id)
  }, [mounted, viewerMin, viewerMax])

  if (!mounted) return null

  const name = pick(names, seed + tickIndex)
  const city = pick(cities, seed * 3 + tickIndex * 7)
  const template = pick(templates, seed + tickIndex * 2)
  const time = pick(TIME_PHRASES, seed * 5 + tickIndex * 3)

  const message = template
    .replace('{name}', name)
    .replace('{city}', city)
    .replace('{time}', time)

  return (
    <StyledWrapper role="status" aria-live="polite">
      <StyledRow>
        <StyledPulseDot />
        <Typography variant="body2" fontWeight={600}>
          {viewerCount} viewing now
        </Typography>
        <StyledDivider />
        <StyledCartGroup>
          <Icon icon={faCartShopping} sx={{ fontSize: pxToRem(14), color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            <strong>{cartCount}</strong> in cart
          </Typography>
        </StyledCartGroup>
      </StyledRow>
      <Fade in={show} timeout={FADE_MS}>
        <StyledTickerRow>
          <Icon
            icon={faCircleUser}
            sx={{ fontSize: pxToRem(18), color: 'primary.main', flexShrink: 0 }}
          />
          <Typography variant="body2" color="text.secondary" noWrap>
            {message}
          </Typography>
        </StyledTickerRow>
      </Fade>
    </StyledWrapper>
  )
}

const StyledWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(8)};
  margin: ${pxToRem(16)} 0 ${pxToRem(4)};
`

const StyledRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
`

const StyledCartGroup = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(6)};
`

const StyledTickerRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
  min-width: 0;
  min-height: ${pxToRem(22)};

  & > p {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const StyledDivider = styled(Box)`
  width: 1px;
  height: ${pxToRem(16)};
  background-color: ${(props) => props.theme.palette.divider};
`

const StyledPulseDot = styled(Box)`
  position: relative;
  width: ${pxToRem(10)};
  height: ${pxToRem(10)};
  border-radius: 50%;
  background-color: #22c55e;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    inset: ${pxToRem(-4)};
    border-radius: 50%;
    background-color: rgba(34, 197, 94, 0.4);
    animation: pixie-live-pulse 1.6s ease-out infinite;
  }

  @keyframes pixie-live-pulse {
    0% {
      transform: scale(0.5);
      opacity: 1;
    }
    100% {
      transform: scale(2.2);
      opacity: 0;
    }
  }
`
